import React, { useState, useEffect, useCallback, useRef } from 'react';
import sendData from 'redux/actions/sendData';
import { first, get, set, camelCase, uniqBy, debounce } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useSupersets } from 'global/hooks/useSupersets';
import { _size, cleanCopy, findOne } from '../../utils';
import { uploadImageToPerform } from '../../../redux/actions/imageUploader';
import { isLocal } from './index';

const DISPLAY_URL_KEYS = ['fullUrl', 'full', 'largeUrl', 'large-url', 'large', 'url', 'thumbUrl', 'thumb-url', 'thumb'];

const signedParam = (params, name) => params.get(name) || params.get(name.toLowerCase());

// S3 temporary URLs from the API expire (default 120 minutes). X-Amz-Date is
// the signature start; X-Amz-Expires is the lifetime in seconds. Older
// signatures put a unix timestamp in Expires.
export const signedUrlExpiresAt = (url) => {
  if (!url || typeof url !== 'string') return null;
  let params;
  try {
    params = new URL(url, 'https://localhost').searchParams;
  } catch (err) {
    return null;
  }

  const amzDate = signedParam(params, 'X-Amz-Date');
  const amzExpires = signedParam(params, 'X-Amz-Expires');
  if (amzDate && amzExpires) {
    const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(amzDate);
    const seconds = Number(amzExpires);
    if (!match || !Number.isFinite(seconds)) return null;
    const start = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6]));
    return start + seconds * 1000;
  }

  const expires = signedParam(params, 'Expires');
  if (expires && /^\d+$/.test(expires)) {
    const n = Number(expires);
    return n < 1e12 ? n * 1000 : n;
  }

  return null;
};

export const isMediaExpired = (media) => {
  if (!media || isLocal(media)) return false;
  const url = DISPLAY_URL_KEYS.map((key) => get(media, key)).find((value) => !!value);
  if (!url) return true;
  const expiresAt = signedUrlExpiresAt(url);
  if (expiresAt == null) return false;
  return Date.now() >= expiresAt;
};

export const createKeyBindings = (opts = {}) => {
  const keydown = function(event) {
    event.preventDefault();
    const ename = `on${event.key}`;
    opts[ename] && opts[ename](event);
  }

  const wheel = function(e) {
    opts.onScroll && opts.onScroll(e);
  };

  const mouseevent = function(_name, e) {
    const name = `on_${_name}`;
    opts[name] && opts[name](e);
  }

  const windows = {
    document: document,
    window: window,
  }
  
  const listeners = [
    { name: 'keyup', func: keydown, window: windows.document },
    { name: 'wheel', func: wheel, window: opts.element || windows.window },
    { name: 'mousemove', func: mouseevent.bind(this, 'mousemove'), window: opts.element || windows.document },
    { name: 'mousedown', func: mouseevent.bind(this, 'mousedown'), window: opts.element || windows.document },
    { name: 'mouseup', func: mouseevent.bind(this, 'mouseup'), window: opts.element || windows.document },
    { name: 'mouseleave', func: mouseevent.bind(this, 'mouseleave'), window: opts.element || windows.document },
  ]

  listeners.map(l => l.window.addEventListener(l.name, l.func));

  return () => {
    listeners.map(l => l.window.removeEventListener(l.name, l.func));
  }
}

export const isHighlight = (collectionName) => {
  return collectionName === 'highlights' || collectionName === 'report-section-bp-images' || collectionName === 'report-section-site-images';
};

const getTypeFromCollectionName = cn => {
  let name = cn;
  if (cn == 'realm-images') {
    name = 'realm-photos'
  } else if (cn == 'scorecard-score-images') {
    name = 'scorecard-scores'
  } else if (cn == 'sub-score-images') {
    name = 'sub-scores'
  }

  return name;
}

const DefaultValues = {
  quality: null,
  supersets: [],
  location: null,
  custom_properties: {
    caption: '',
  }
};

export const useEditData = (props) => {
  const [state, setState] = useState({
    loading: true,
    updating: false,
    hasEdits: false,
    mediaChanges: {},
  })
  const [values, _setValues] = useState(cleanCopy(DefaultValues));

  const imagetype = get(props, 'imagetype', 'realm-photos');
  const parentId = get(props, 'parentId'); 
  const imageid = get(props, 'id');
  const onPhotoUpdatedRef = useRef(props.onPhotoUpdated);
  onPhotoUpdatedRef.current = props.onPhotoUpdated;

  const publishPhoto = (photo) => {
    if (photo && photo.id != null && onPhotoUpdatedRef.current) {
      onPhotoUpdatedRef.current(photo);
    }
  };

  //load the assessment
  const assessment = get(props, 'assessment.data', get(props, 'assessment'));
  const aid = get(assessment, 'id', null);
  const adata = useRetrieveData(
    { url: `/assessments/${aid}` },
    {
      waitFor: aid,
      setLoadingEvenIfWait: true,
      useEffect: true,
      build: ['assessments'],
      useCache: { reload: 2 },
      overrideNotFound: true,
      params: {
        include: 'realm,realm.realms,authors',
      },
      transformData: (d) => {
        const ret = first(d);

        const locations = uniqBy(
          [
            get(ret, 'realm'),
            ...get(ret, 'realm.realms', []).sort((a, b) => {
              const A = get(a, 'name');
              const B = get(b, 'name');
              return A === B ? 0 : A > B ? 1 : -1;
            }),
          ],
          'id'
        );

        return {
          ...ret,
          locations,
        };
      },
    }
  );

  //load supersets
  const { supersets } = useSupersets({ assessment });

  //the parent collection data
  const imgdata = useDataRetriever(props);

  //the actual media data
  const local = isLocal(props);
  // Realm photos already carry a signed URL. Other editors, including scorecard
  // scores, need the media record so the new upload keeps previousModelId and
  // previousModelType.
  const realmPhoto = ['realm-images', 'realm-photos'].includes(imgdata.collectionName);
  const refreshMedia = !local && (!realmPhoto || isMediaExpired(props) || !!_size(state.mediaChanges));
  const baseMediaId = (() => {
    if (local) { return null }
    const alwaysUseImageId = get(props, 'alwaysUseImageId', false);
    return get(props, 'mediaId', alwaysUseImageId || imgdata.collectionName.includes('realm-') ? imageid : get(imgdata, 'data.images.0.id', get(props, 'id', imageid)));
  })();
  const media_id = local ? null : get(state, `mediaChanges.${baseMediaId}`, baseMediaId);

  const mediadata = useRetrieveData({ url: `/media/${media_id}` }, {
    waitFor: refreshMedia ? media_id : null,
    setLoadingEvenIfWait: true,
    useEffect: true,
    build: ['media'],
    transformData: d => first(d),
  })

  const publishedRefreshRef = useRef();
  useEffect(() => {
    if (local || !refreshMedia) return;
    const media = mediadata.data;
    if (!get(media, 'id')) return;
    // Realm-photo reads are skipped while the URL is valid, so imgdata is empty
    // after an edit. The open photo is still the list record.
    const photo = get(imgdata, 'data.id') ? imgdata.data : { id: get(props, 'parentId', imageid), images: get(props, 'images') };
    if (!get(photo, 'id')) return;
    const key = `${photo.id}:${media.id}:${get(media, 'fullUrl', get(media, 'url', ''))}`;
    if (publishedRefreshRef.current === key) return;
    publishedRefreshRef.current = key;

    const rawImages = _size(photo.images) ? photo.images : [];
    const images = rawImages.some((img) => img && (img.realm || img.photos)) ? [{}] : (_size(rawImages) ? rawImages : [{}]);
    const mergedImages = images.map((img, i) => {
      const matches = String(img.id) === String(media.id) || String(img.mediaId) === String(media.id) || String(img.mediaId) === String(media.mediaId);
      if (!matches && images.length > 1) return img;
      if (!matches && i !== 0) return img;
      return { ...img, ...media, id: media.id };
    });
    const urls = {};
    DISPLAY_URL_KEYS.forEach((keyName) => {
      if (media[keyName]) urls[keyName] = media[keyName];
    });
    publishPhoto({
      id: photo.id,
      ...urls,
      images: mergedImages,
    });
  }, [imgdata.data, mediadata.data, local, imageid, refreshMedia]);

  //actually update the media properties
  const updateMediaProperties = useCallback(async (vals) => {
    try {
      await sendData({ 
        url: `/media/${media_id}`,
        method: 'patch',
        data: {
          data: {
            type: 'media',
            id: `${media_id}`,
            attributes: {
              custom_properties: vals.custom_properties
            }
          }
        }
      })
    } catch (err) {
      console.error(err);
    }
  }, [media_id]);

  //send requests to update image values
  const updateValues = useCallback(debounce(async (vals = {}) => {
    try {
      const id = parentId || imageid;
      if (!id) { return }

      if (local) { return }

      setState(s => ({ ...s, updating: true }));

      const collectionName = get(props, 'collection', get(imgdata, 'collectionName'));
      // Library checklist/checkpoint media is form-owned (no parent PATCH target like
      // /realm-photos/{id}). Only update media custom_properties — same as imagetype media.
      const isFormManagedNoParent =
        props.childImagesIncludedInEdit && !parentId &&
        (collectionName === 'checkpoint-images' || collectionName === 'checklist-images');
      // Sub-score photos use the same default imagetype as realm photos, but parentId
      // is the sub-score. Caption belongs on this media record and the form image.
      const isSubScoreImage = collectionName === 'sub-score-images';
      // autoSave false means this editor persists captions with the form submit.
      // Omitted means the editor keeps its own debounced media update.
      const deferCaptionToSubmit = get(props, 'autoSave') === false;

      let resp;
      if (imagetype !== 'media' && !isFormManagedNoParent && !isSubScoreImage) {
        const data = {
          data: {
            type: imagetype,
            id: `${id}`,
            attributes: {
              name: vals.name || get(props, 'name'),
              ...vals,
              realm_id: get(vals, 'location'),
              supersets: undefined,
              location: undefined,
            },
            relationships: {
              supersets: {
                data: (vals.supersets || []).map(s => ({ id: `${s}`, type: 'supersets' }))
              }
            }
          }
        }

        const include = imagetype === 'realm-photos' ? '?include=supersets,realm,images' : '';
        resp = await sendData(
          {
            url: `/${imagetype}/${id}${include}`,
            method: 'patch',
            data,
          },
          { normalize: { build: [camelCase(imagetype)] } }
        ).catch(console.error);
        publishPhoto(first(resp));
      }

      if (!deferCaptionToSubmit) {
        await updateMediaProperties(vals);
      }

      // Caption lives on the media record. The realm-photo response above does
      // not include it, so write it onto the photo the grid reopens.
      const caption = get(vals, 'custom_properties.caption');
      if (!deferCaptionToSubmit && caption !== undefined) {
        publishPhoto({
          id: parentId || imageid,
          caption,
          custom_properties: vals.custom_properties,
          customProperties: vals.custom_properties,
        });
      }

      return first(resp);
    } catch (err) {
      console.error(err);
    } finally {
      setState(s => ({ ...s, updating: false }));
    }
  }, 1000), [media_id, imageid, imgdata.data, parentId, imagetype, local]); 

  const updateMediaImageId = img => {
    setState(s => ({
      ...s,
      mediaChanges: {
        ...s.mediaChanges,
        [baseMediaId]: img.id,
      },
    }))
  }

  const updateImageDataRef = useCallback(async(img) => {
    try {
      const isDel = img == null;
      const collectionName = get(imgdata, 'collectionName');
      const type = getTypeFromCollectionName(collectionName);

      // For library checklist/checkpoint media, the Media dialog is opened from a
      // form (DocumentChecklist EDIT MEDIA) that owns the image array and persists
      // changes itself on submit via PATCH /checkpoints/{id} or PATCH /checklists/{id}.
      // We don't have a parentId here for those collections, and there is no direct
      // REST resource at `/checkpoint-images/{id}` or `/checklist-images/{id}`, so
      // skip the API call and let the parent form persist via onValuesChange.
      // (PFM-2577)
      const isFormManagedNoParent =
        props.childImagesIncludedInEdit && !parentId &&
        (collectionName === 'checkpoint-images' || collectionName === 'checklist-images');

      if (isFormManagedNoParent) {
        if (!isDel) {
          updateMediaImageId(img);
        }

        setState(s => ({ ...s, hasEdits: true }));

        // Pass `newimg` so the parent DropArea/form replaces the thumbnail with the
        // edited media (same contract as uploadAllNew). Without it, annotations were
        // discarded and only caption/archive paths reached the form.
        props.onValuesChange && props.onValuesChange({
          id: media_id,
          imagetype,
          values,
          archived: isDel,
          ...(isDel ? {} : { newimg: img }),
        });

        return;
      }

      const id = parentId || imageid;

      const data = {
        data: {
          type: type,
          id: `${id}`,
          attributes: isDel && type == 'realm-photos' ? { archived: true } : undefined,
        }
      }

      let images = isDel ? [] : [img];
      if (props.childImagesIncludedInEdit) {
        images = [...props.images.filter(im => im.id != media_id), ...images];
      }

      set(data, 'data.relationships.images.data', images.map(im => ({ type: 'media', id: `${im.id}` })))

      const include = type === 'realm-photos' ? '?include=supersets,realm,images' : '';
      const resp = await sendData(
        {
          url: `/${type}/${id}${include}`,
          method: 'patch',
          data,
        },
        { normalize: { build: [camelCase(type)] } }
      ).catch(e => {
        if (e.toString().includes('does not exist')) {
          return {};
        }
        throw e;
      });

      if (isDel) {
        publishPhoto({ ...(first(resp) || {}), id, archived: true });
      } else {
        publishPhoto(first(resp));
        updateMediaImageId(img);
      }

      setState(s => ({ ...s, hasEdits: true }));

      props.onValuesChange && props.onValuesChange({
        id: media_id,
        imagetype,
        values,
        archived: isDel,
      })

      return get(resp, 'data.data');
    } catch (err) {
      console.error(err);
    }
  }, [imageid, media_id, imgdata.data]);

  const updateImage = useCallback(async (vals = {}) => {
    try {
      setState(s => ({ ...s, updating: true }));

      const img = { 
        ...vals,
        file: vals.imageBase64, 
        name: get(mediadata, 'data.fileName', vals.fullName),
        collection: get(props, 'collection', get(mediadata, 'data.collectionName', get(imgdata, 'collectionName'))),
        custom_properties: {
          ...(values.custom_properties || {}),
          annotation: true,
          originalId: get(mediadata, 'data.customProperties.originalId', get(mediadata, 'data.custom_properties.originalId', get(props, 'images.0.customProperties.originalId', get(props, 'customProperties.originalId', media_id)))),
        },
      };

      const resp = await uploadImageToPerform(img);

      if (props.uploadAllNew) {
        updateMediaImageId(resp);
        props.onValuesChange && props.onValuesChange({
          id: media_id,
          imagetype,
          values,
          newimg: resp,
        })
        return;
      }

      await updateImageDataRef(resp);
    } catch (err) {
      console.error(err);
    } finally {
      setState(s => ({ ...s, updating: false }))
    }
  }, [values, imageid, imgdata.data, mediadata.data]);

  const loadedMedia = get(mediadata, 'data');
  const originalId = get(loadedMedia, 'id')
    ? get(loadedMedia, 'customProperties.originalId', get(loadedMedia, 'custom_properties.originalId'))
    : get(props, 'images.0.customProperties.originalId', get(props, 'customProperties.originalId'));

  const getOriginalImage = useCallback(async () => {
    try {
      if (originalId) {
        const resp = await sendData({ url: `/media/${originalId}` }, {
          normalize: { build: ['media'] }
        })

        return first(resp);
      }
    } catch (err) {
      console.error(err)    ;
    }
  }, [originalId]);

  const setOriginalImage = useCallback(async (opts = {}) => {
    opts = {
      confirm: true,
      ...opts,
    }

    const goahead = opts.confirm ? window.confirm('Are you sure? This cannot be undone.') : true;

    if (goahead) {
      const orig = await getOriginalImage();
  
      await sendData({ 
        url: `/media/${orig.id}`,
        method: 'patch',
        data: {
          data: {
            type: 'media',
            id: `${orig.id}`,
            attributes: {
              custom_properties: {
                ...(values.custom_properties || {}),
                annotation: undefined,
                originalId: undefined,
              }
            }
          }
        }
      })

      if (props.uploadAllNew) {
        updateMediaImageId(orig);
        props.onValuesChange && props.onValuesChange({
          id: media_id,
          imagetype,
          values,
          newimg: orig,
        })
        return;
      }
  
      await updateImageDataRef(orig);
    }
  }, [values, media_id, mediadata])

  const deleteImage = useCallback(async (opts = {}) => {
    opts = {
      confirm: true,
      ...opts,
    }

    const goahead = opts.confirm ? window.confirm('Are you sure? This cannot be undone.') : true;

    if (goahead) {
      setState(s => ({ ...s, hasEdits: true }))

      await updateImageDataRef(null);
    }
  }, [values, media_id, mediadata])

  useEffect(() => {
    const isLoading = adata.loading || imgdata.loading;

    if (isLoading) {
      setState(s => ({ ...s, loading: isLoading }))
    } else {
      setTimeout(() => {
        setState(s => ({ ...s, loading: isLoading }))
      }, 50);
    }

  }, [adata.loading, imgdata.loading]);

  //set the original values
  useEffect(() => {
    if (get(imgdata, 'data.id') && !imgdata.loading && !local) {
      _setValues((v) => ({
        ...v,
        quality: get(imgdata, 'data.quality', null),
        location: get(props, 'realm.id', get(imgdata, 'data.realm.id')),
        supersets: get(imgdata, 'data.supersets', []).map(ss => ss.id)
      }));
    }
  }, [imgdata.data, imgdata.loading]);

  useEffect(() => {
    if (local) return;
    const caption = get(props, 'caption', get(props, 'customProperties.caption', get(props, 'custom_properties.caption', ''))) || '';
    // Sub-score photos refresh the media record, so this used to wait for that
    // request and left the previous caption in the field. Show this slide now.
    if (refreshMedia) {
      _setValues((v) => ({
        ...v,
        custom_properties: {
          ...get(props, 'customProperties', {}),
          ...get(props, 'custom_properties', {}),
          caption,
        },
      }));
      return;
    }
    const supersets = get(props, 'supersets', []);
    _setValues((v) => ({
      ...v,
      quality: get(props, 'quality', null),
      location: get(props, 'realm.id', get(props, 'realmId', null)),
      supersets: (Array.isArray(supersets) ? supersets : []).map((ss) => (ss && typeof ss === 'object' ? ss.id : ss)).filter((id) => id != null),
      custom_properties: {
        ...get(props, 'customProperties', {}),
        ...get(props, 'custom_properties', {}),
        caption,
      },
    }));
  }, [local, refreshMedia, imageid]);

  //set the customproperties which actually exist on the media item
  useEffect(() => {
    if (local) {
      _setValues((v) => ({
        ...v,
        quality: get(props, 'quality', v.quality),
        supersets: get(props, 'supersets', v.supersets),
        location: get(props, 'realmId', v.location),
        custom_properties: {
          ...get(props, 'custom_properties', {}),
          caption: get(props, 'custom_properties.caption', get(props, 'caption', '')) || '',
        },
      }));
    } else if (get(mediadata, 'data.id') && !mediadata.loading && String(get(mediadata, 'data.id')) === String(media_id)) {
      const cprops = {
        ...get(mediadata, 'data.customProperties', {}),
        ...get(props, 'custom_properties', {}),
      };
      _setValues((v) => ({
        ...v,
        custom_properties: {
          ...cprops,
          caption: get(cprops, 'caption') || get(props, 'caption') || '',
        },
      }));
    }

    setState(s => ({ ...s, loading: mediadata.loading }));
  }, [mediadata.data, mediadata.loading, local, media_id]);

  const setValues = func => {
    _setValues(vv => {
      const newv = func(vv);
      // Notify the parent form immediately (not via the debounced API update) so
      // DropArea caption fields remount with the latest value when fullscreen closes.
      props.onValuesChange && props.onValuesChange({
        id: media_id,
        imagetype,
        values: newv,
      });
      updateValues(newv);
      return newv;
    })
    setState(s => ({ ...s, hasEdits: true }));
  }

  return {
    ...state,
    updateValues,
    updateImage,
    getOriginalImage,
    setOriginalImage,
    deleteImage,

    mediadata: get(mediadata, 'data', {}),
    originalId,
    imgdata: get(imgdata, 'data', {}),
    assessment: get(adata, 'data', {}),
    realm: get(props, 'realm') || get(imgdata, 'data.realm') || get(adata, 'data.realm'),
    collectionName: get(imgdata, 'collectionName'),
    supersets,
    hasValidData: !!imageid,
    
    values,
    setValues,
  };
};

export const useDataRetriever = (props) => {
  const [params, setParams] = useState([{ url: '/' }, { waitFor: null, setLoadingEvenIfWait: true, }]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const collectionName = get(props, 'collection', get(props, 'collectionName', get(props, 'collection_name', get(props, 'imagetype', 'realm-photos'))));
  const pid = findOne(props, ['parentId', 'scorecardScoreId', 'scorecard_score.id', 'task.id', 'report_section.id']) || props.id;
  const local = isLocal(props);
  const mediaExpired = isMediaExpired(props);

  const getMore = async (data, key) => {
    try {
      setLoading(true);

      if (key === 'processedScorecardScoreValues') {
        const resp = await sendData(
          {
            url: `/processed-scorecard-score-values`,
          },
          {
            normalize: {
              build: [key],
            },
            params: {
              filter: {
                scorecard_id: data.scorecardId,
                assessment_id: data.assessmentId,
              },
              include: 'superset,assessment',
            },
          }
        );
        const found = resp.find((f) => f.scorecardScoreId == pid);
        setData({ ...data, [key]: found });
      } else if (key === 'scorecards') {
        const resp = await sendData(
          {
            url: `/scorecards/${data.scorecardId}`,
          },
          {
            normalize: {
              build: [key],
            },
          }
        );
        setData({ ...data, [key]: first(resp) });
      }
    } catch (err) {
      console.error(err);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (local) { return }

    let obj1 = {}, obj2 = { waitFor: null, setLoadingEvenIfWait: true, };

    if (collectionName === 'scorecard-score-images') {
      // const scid = get(props, 'parentId', get(props, 'scorecard_score.id', get(props, 'scorecardScoreId')));
      obj1 = { url: `/scorecard-scores/${pid}` };
      obj2 = { build: ['scorecardScores'], transformData: (d) => first(d) };
    } else if (collectionName === 'task-images' || collectionName === 'comment-images') {
      obj1 = { url: `/tasks/${pid}` };
      obj2 = {
        build: ['tasks'],
        transformData: (d) => first(d),
        overrideNotFound: true,
        params: {
          include: 'authors,images,realm',
        },
      };
    } else if (isHighlight(collectionName)) {
      const mediaId = get(props, 'mediaId', props.id);
      const sectionId = findOne(props, ['parentId', 'reportSectionId', 'report_section.id', 'report_section_id']);
      if (!sectionId || String(sectionId) === String(mediaId)) {
        setParams([{ url: '/' }, { waitFor: null, setLoadingEvenIfWait: true }]);
        setData({});
        return;
      }
      obj1 = { url: `/report-sections/${sectionId}` };
      obj2 = {
        build: ['reportSections'],
        transformData: (d) => first(d),
        overrideNotFound: true,
        params: {
          include: 'user,assessment,images,superset',
        },
      };
    } else if (['realm-images', 'realm-photos'].includes(collectionName)) {
      if (!mediaExpired) {
        setParams([{ url: '/' }, { waitFor: null, setLoadingEvenIfWait: true }]);
        setData({});
        return;
      }
      obj1 = { url: `/realm-photos/${pid}` };
      obj2 = !pid ? obj2 : {
        build: ['realmPhotos'],
        transformData: (d) => first(d),
        overrideNotFound: true,
        params: {
          include: 'supersets,realm,images',
        },
      };
    }

    if (obj1 || obj2) {
      setParams([obj1, obj2]);
    }

    setData({});
  }, [collectionName, pid, local, mediaExpired]);

  const ret = useRetrieveData(...params);

  useEffect(() => {
    if (ret.data && !ret.loading) {
      if (collectionName === 'scorecard-score-images' && _size(ret.data)) {
        getMore(ret.data, 'scorecards');
      } else {
        setData(ret.data)
      }
    }
  }, [ret.data, ret.loading, pid, collectionName]);

  return {
    ...ret,
    data,
    loading: ret.loading || loading,
    collectionName,
  };
};